import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, Button, DropdownField, InputField, NavLink, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { simulationAgentActions } from "../../redux/reducers/simulationAgentSlice";
import { agentActions } from "../../redux/reducers/agentSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useCoachOverlay } from "../../components/onboarding/useCoachOverlay";
import { newSimulationOverlay } from "../../components/onboarding/coachOverlays";

type SimulationAgentFormValues = {
  name: string;
  systemPrompt: string;
  agentId: string;
};

const defaultFormState: SimulationAgentFormValues = {
  name: "",
  systemPrompt: "",
  agentId: "",
};

export const NewSimulationAgentPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const agents = useAppSelector((state) => Object.values(state.agent.entities));
  const agentsIsLoading = useAppSelector((state) => state.agent.isLoading);
  const isSaving = useAppSelector((state) => state.simulationAgent.isLoadingCreate);

  useCoachOverlay(newSimulationOverlay);

  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<SimulationAgentFormValues>(defaultFormState);

  useEffect(() => {
    dispatch(agentActions.get({}));
  }, [dispatch]);

  const handleFormChange =
    <K extends keyof SimulationAgentFormValues>(key: K) =>
    (value: SimulationAgentFormValues[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const handleCancel = () => {
    navigate("/agents/simulations");
  };

  const handleSubmit = async () => {
    if (isSaving) {
      return;
    }

    try {
      const name = form.name.trim();
      if (!name) {
        throw new Error("Name is required.");
      }

      if (!form.agentId) {
        throw new Error("Please select an agent.");
      }

      setFormError(null);
      await dispatch(
        simulationAgentActions.create({
          name,
          systemPrompt: form.systemPrompt,
          agentId: form.agentId,
        }),
      ).unwrap();
      navigate("/agents/simulations");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to create simulation agent");
    }
  };

  return (
    <View className="flex flex-col flex-1 w-full min-h-full gap-6 p-6 bg-white dark:bg-slate-900 overflow-y-auto">
      <NavLink
        href="/agents/simulations"
        className="flex items-center w-fit p-0"
      >
        <Icon
          name="arrow-back"
          size={18}
          className="mr-1"
        />
        Simulations
      </NavLink>

      <View className="flex flex-col gap-6 w-full">
        <View className="flex flex-col gap-2">
          <Text className="text-2xl font-semibold text-gray-900 dark:text-white">Create Simulation Agent</Text>
          <Text className="text-sm text-gray-600 dark:text-gray-400">
            Create a simulation agent to test different prompts and behaviors without impacting live environments.
          </Text>
        </View>

        {formError ? (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}

        <View className="flex flex-col gap-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm w-full">
          <InputField
            label="Name"
            placeholder="Enter simulation agent name"
            value={form.name}
            onChange={handleFormChange("name")}
          />

          <DropdownField
            label="Agent"
            options={agents.map((agent) => ({
              value: agent.id,
              label: agent.name,
            }))}
            value={form.agentId}
            onChange={handleFormChange("agentId")}
            placeholder="Select an agent"
            readOnly={agentsIsLoading}
          />
          {agentsIsLoading && <Text className="text-xs text-gray-500 dark:text-gray-400 -mt-2">Loading agents...</Text>}

          <div data-onboarding="sim-prompt">
            <InputField
              label="System Prompt"
              placeholder="Describe the simulation agent's behavior"
              value={form.systemPrompt}
              onChange={handleFormChange("systemPrompt")}
              type="textarea"
              className="min-h-[128px]"
            />
          </div>
        </View>

        <View className="flex flex-row gap-3 justify-end">
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <div data-onboarding="sim-run">
            <Button
              variant="success"
              size="sm"
              outline
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="add"
                  size={14}
                />
              )}
              Create Simulation Agent
            </Button>
          </div>
        </View>
      </View>
    </View>
  );
};
